import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User } from "lucide-react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { setAccessToken } from "../../api/axiosInstance";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

function RegisterCanvas() {
  useAlphabeticInput();
  const navigate = useNavigate();
  
  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName) newErrors.firstName = "Please Enter First Name";
    if (!formData.lastName) newErrors.lastName = "Please Enter Last Name";
    
    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Enter Confirm Password";
    }

    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Password & Confirm Password must be same";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const res = await userAPI.register(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setAccessToken(res.data.access_token);

        const user = {
          email: res.data.email,
          username: res.data.username,
        };

        localStorage.setItem("token", JSON.stringify(user));
        localStorage.setItem("accessToken", res.data.accessToken);
        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <div className="register-page-funky">
      <Container fluid className="p-0 h-100">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Brand Panel */}
          <Col lg={6} className="d-none d-lg-flex align-items-center justify-content-center custom-register-left-panel">
            <div className="left-panel-content">
              <span className="badge-text mb-3">JOIN THE CLUB</span>
              <h1 className="left-panel-heading">START YOUR SNACK JOURNEY.</h1>
              <p className="left-panel-sub">Create an account to get faster checkouts, track your bags instantly, and unlock fresh snack drops! ⚡</p>
              <div className="funky-floating-sticker">JOIN NOW</div>
            </div>
          </Col>

          {/* Right Side: Register Form Box */}
          <Col lg={6} className="d-flex justify-content-center align-items-center p-4 p-md-5 bg-white">
            <div className="custom-register-formbox">
              <span className="form-badge">SIGN UP</span>
              <h2 className="register-title-funky mt-3 mb-1">Create Account</h2>
              <p className="text-muted mb-4">Join us today to explore premium taste blocks.</p>

              <Form onSubmit={handleSubmit}>
                {/* FIRST NAME & LAST NAME ROW */}
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Label className="funky-form-label mb-1">First Name</Form.Label>
                    <div className="funky-input-wrapper">
                      <User size={18} className="input-icon-left" />
                      <Form.Control
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="funky-form-input"
                      />
                    </div>
                    {errors.firstName && (
                      <small className="text-danger d-block mt-1 fw-bold">{errors.firstName}</small>
                    )}
                  </Col>
                  
                  <Col md={6}>
                    <Form.Label className="funky-form-label mb-1">Last Name</Form.Label>
                    <div className="funky-input-wrapper">
                      <User size={18} className="input-icon-left" />
                      <Form.Control
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="funky-form-input"
                      />
                    </div>
                    {errors.lastName && (
                      <small className="text-danger d-block mt-1 fw-bold">{errors.lastName}</small>
                    )}
                  </Col>
                </Row>

                {/* EMAIL */}
                <Form.Group className="mb-3" controlId="email">
                  <Form.Label className="funky-form-label mb-1">Email Address</Form.Label>
                  <div className="funky-input-wrapper">
                    <Mail size={18} className="input-icon-left" />
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="hello@vibe.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="funky-form-input"
                    />
                  </div>
                  {errors.email && (
                    <small className="text-danger d-block mt-1 fw-bold">{errors.email}</small>
                  )}
                </Form.Group>

                {/* PASSWORD & CONFIRM PASSWORD ROW */}
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Form.Label className="funky-form-label mb-1">Password</Form.Label>
                    <div className="funky-input-wrapper">
                      <Lock size={18} className="input-icon-left" />
                      <Form.Control
                        type="password"
                        name="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        className="funky-form-input"
                      />
                    </div>
                    {errors.password && (
                      <small className="text-danger d-block mt-1 fw-bold">{errors.password}</small>
                    )}
                  </Col>

                  <Col md={6}>
                    <Form.Label className="funky-form-label mb-1">Confirm Password</Form.Label>
                    <div className="funky-input-wrapper">
                      <Lock size={18} className="input-icon-left" />
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="funky-form-input"
                      />
                    </div>
                    {errors.confirmPassword && (
                      <small className="text-danger d-block mt-1 fw-bold">{errors.confirmPassword}</small>
                    )}
                  </Col>
                </Row>

                {/* SUBMIT BUTTON */}
                <button className="register-submit-btn-funky mb-4" type="submit">
                  CREATE ACCOUNT ⚡
                </button>

                {/* LOGIN REDIRECT */}
                <div className="text-center pt-3 border-top-funky">
                  <p className="mb-0 text-muted small fw-medium">
                    Have an account?
                    <Link to="/users/login" className="login-redirect-link ms-2">
                      Login Here
                    </Link>
                  </p>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default RegisterCanvas;